import { Web3State, Web3Store } from '../../../shared/state/web3-store/web3.store';
import {
  Address,
  Chain,
  createPublicClient,
  createWalletClient,
  custom,
  formatEther,
  TransactionReceipt,
} from 'viem';
import {
  arbitrum,
  arbitrumSepolia,
  avalanche,
  avalancheFuji,
  bsc,
  bscTestnet,
  fantom,
  fantomTestnet,
  goerli,
  hardhat,
  mainnet,
  optimism,
  optimismSepolia,
  polygon,
  polygonMumbai,
  sepolia,
} from 'viem/chains';

import { Injectable, OnDestroy } from '@angular/core';
import { IBlockchain, ILoginResponseDto } from '@nft-folks/interfaces';
import { debounceTime, firstValueFrom, Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Web3Query } from '../../../shared/state/web3-store/web3.query';
import { logAction } from '@datorama/akita';
import { AlertService } from '@nft-folks/ui-core';
import { amoyCustom } from './custom-chains';
import { AuthApiService } from '../../services/core-api/auth.service';
import { ErrorType } from 'viem/errors/utils';
import { environment } from '../../../../environments/environment';

const SUPPORTED_CHAINS = [
  hardhat,
  mainnet,
  sepolia,
  // goerli,
  avalanche,
  polygon,
  polygonMumbai,
  // polygonAmoy,
  amoyCustom,
  avalancheFuji,
  bsc,
  bscTestnet,
  fantom,
  fantomTestnet,
  arbitrum,
  arbitrumSepolia,
  optimism,
  optimismSepolia,
];

@Injectable({ providedIn: 'root' })
export class ViemService implements OnDestroy {
  publicClient: ReturnType<typeof createPublicClient>;
  walletClient: ReturnType<typeof createWalletClient>;
  provider: any = null;

  constructor(
    private alertService: AlertService,
    private web3Store: Web3Store,
    private web3Query: Web3Query,
    private authApiService: AuthApiService,
  ) {
    this.setUpProvider();
    this.listenChainConnection();
  }

  private subscription = new Subscription();
  private chain: Chain | undefined;
  private account: any;

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private setUpProvider() {
    if (typeof (window as any).ethereum !== 'undefined' && !this.provider) {
      this.provider = custom((window as any).ethereum);
      this.web3Store.update({ hasEthProvider: true });
      try {
        this.publicClient = createPublicClient({
          chain: this.chain || mainnet, // Fallback to a default chain if undefined
          transport: custom((window as any).ethereum),
        });
      } catch (error) {
        console.log('Something went wrong, plase check your wallet config', error);
      }

      //Create wallet client to interact with ledger.Sign transactions
      try {
        this.walletClient = createWalletClient({
          account: this.account,
          chain: this.chain,
          transport: custom((window as any).ethereum),
        });
      } catch (error) {
        console.log('Could not create wallet client', error);
      }
      this.initializeMetaMaskListeners();
      this.setInitialAccountAndChain();
    } else {
      this.web3Store.update({ hasEthProvider: false });
    }
  }

  private async setInitialAccountAndChain() {
    const accounts = await this.getAccounts();
    const chainId = await this.getChainId();
    logAction('Set Initial Account and Chain');
    this.web3Store.update({
      chainId,
      accounts,
      address: accounts[0],
    });
  }

  private initializeMetaMaskListeners() {
    const ethereum = (window as any).ethereum;

    ethereum.on('accountsChanged', (accounts: string[]) => {
      logAction('On accounts changed event');
      if (accounts.length > 0) {
        this.web3Store.update(
          (state: Web3State) =>
            ({
              ...state,
              adapterEvents: 'connected',
              chainId: state.chainId,
              accounts,
              address: accounts[0],
            } as Web3State),
        );
      } else {
        this.web3Store.update(
          (state: Web3State) =>
            ({
              ...state,
              adapterEvents: 'disconnected',
              chainId: state.chainId,
              accounts: [],
              address: null,
            } as Web3State),
        );
      }
    });

    ethereum.on('chainChanged', async (chainId: string) => {
      const newChainId = parseInt(chainId, 16);
      logAction('On chainId changed event');
      this.web3Store.update({
        chainId: newChainId,
      });
    });
  }

  public get isSupportedChain$(): Observable<boolean> {
    return this.web3Query.chainId$.pipe(
      startWith(this.web3Query.chainId$),
      map((chainId) => {
        return SUPPORTED_CHAINS.some((chain) => chain.id === chainId);
      }),
      debounceTime(200),
    );
  }

  async getAccount(): Promise<Address> {
    const [address] = await this.walletClient.requestAddresses();
    return address;
  }

  async getAccounts(): Promise<Address[]> {
    return this.walletClient.requestAddresses();
    // return this.walletClient.getAddresses();
  }

  private listenChainConnection() {
    this.subscription.add(
      this.isSupportedChain$.subscribe((isSupported) => {
        if (!isSupported) {
          this.alertService.warning('chainNotSupported');
          console.log('Not supported');
        }
      }),
    );
  }

  async login(): Promise<ILoginResponseDto> {
    // Prompt the user to connect their wallet if no accounts are available
    const ethereum = (window as any).ethereum;
    let accounts = await ethereum.request({ method: 'eth_accounts' });
    if (!accounts || accounts.length === 0) {
      accounts = await ethereum.request({ method: 'eth_requestAccounts' });
      if (!accounts || accounts.length === 0) {
        throw new Error('Wallet connection was not established');
      }
    }

    // Set the connected account and chain in the store
    this.web3Store.update({
      accounts,
      address: accounts[0],
    });

    const address = accounts[0];

    if (address) {
      const nonce = await this.publicClient
        .getTransactionCount({
          address,
        })
        .catch((error) => {
          console.log(error);
          throw error;
        });
      const messageToSign = `
Sign this message to log in to ${environment.appName}



Nonce: ${nonce}
Generated: ${new Date(Date.now()).toISOString()}
    `;

      const signature = await this.walletClient.signMessage({
        account: address,
        message: messageToSign,
      });

      return firstValueFrom(
        this.authApiService.verifyBasic({
          signature: signature,
          message: messageToSign,
          address,
        }),
      );
    } else {
      return Promise.reject('No Ethereum provider found');
    }
  }

  async logOut() {
    // No action needed for MetaMask
    console.log('logged out');
    // Optionally, clear any stored data
    this.provider = null;
  }

  async changeChainIfNeeded(newChainId: number) {
    const currentChainId: number = await this.getChainId();
    if (currentChainId !== newChainId) {
      const foundChain = this.getChain(newChainId);
      if (!foundChain) {
        throw new Error(`Chain '${newChainId}' not found.`);
      }
      await this.walletClient.addChain({ chain: foundChain });
      await this.walletClient.switchChain({ id: newChainId });
    }
  }

  private getChain(chainId: number): Chain {
    const foundChain = SUPPORTED_CHAINS.find((supportedChain) => {
      return supportedChain.id === chainId;
    });
    if (!foundChain) {
      throw new Error(`Blockchain with id ${chainId} not found.`);
    }
    return foundChain;
  }

  /**
   * Get the connected Chain's ID
   */
  async getChainId(): Promise<number> {
    if (!this.publicClient) {
      return Promise.reject();
    }
    try {
      this.web3Store.setIsFetchingData(true);
      const network = await this.publicClient.getChainId();
      this.web3Store.setIsFetchingData(false);
      return network;
    } catch (error) {
      this.web3Store.setIsFetchingData(false);
      console.error(error);
      return Promise.reject(error);
    }
  }

  /**
   * Get user's Ethereum public address
   */
  async getAddress(): Promise<Address | void> {
    if (!this.walletClient) {
      return Promise.reject();
    }
    try {
      this.web3Store.setIsFetchingData(true);
      const address = await this.walletClient.getAddresses().then((addresses) => addresses[0]);
      this.web3Store.setIsFetchingData(false);
      return address;
    } catch (error) {
      this.web3Store.setIsFetchingData(false);
      console.error(error);
      return Promise.reject(error);
    }
  }

  /**
   * Get total supply of tokens
   */
  async getTotalSupply(abi: any, contractAddress: Address, chainId: number): Promise<number> {
    const res = await this.readContract<bigint>({
      abi,
      contractAddress: contractAddress,
      functionName: 'totalSupply',
      chainId: chainId,
    });
    return Number(res);
  }

  /**
   * Get user's balance in ether
   */
  async getBalance({ chainId }: { chainId: number }): Promise<string> {
    try {
      this.web3Store.setIsFetchingData(true);
      const address = await this.getAddress();
      if (!address) throw new Error('Address not found');
      await this.changeChainIfNeeded(chainId);
      const balanceWei = await this.publicClient.getBalance({
        address,
      });
      const balance = formatEther(balanceWei);
      this.web3Store.setIsFetchingData(false);
      return balance;
    } catch (error) {
      this.web3Store.setIsFetchingData(false);
      console.error(error);
      return Promise.reject(error);
    }
  }

  /**
   * Publish (deploy) a contract
   */
  async publishContract(
    abi: any,
    bytecode: any,
    args: (string | number)[],
    blockchain: IBlockchain,
  ): Promise<{ hash: string; from: Address; nonce: number; contractAddress: Address }> {
    try {
      this.web3Store.resetLastTransaction();
      this.web3Store.setIsTransaction(true);
      const [address] = await this.walletClient.getAddresses();
      const parsedAbi = JSON.parse(abi);
      await this.changeChainIfNeeded(blockchain.chainId);
      const chain = this.getChain(blockchain.chainId);
      const hash = await this.walletClient.deployContract({
        abi: parsedAbi,
        account: address,
        bytecode,
        args,
        chain,
      } as any);

      this.web3Store.setTxHash(hash);
      const txReceipt = await this.publicClient.waitForTransactionReceipt({ hash });
      if (txReceipt.status !== 'success' || !txReceipt.contractAddress) {
        console.log(txReceipt);
        throw new Error('Transaction is not successfull');
      }
      const nonce = await this.publicClient.getTransactionCount({
        address,
      });
      this.web3Store.setIsTransaction(false);
      this.web3Store.setTxReceipt(txReceipt);
      return { hash, from: address, nonce, contractAddress: txReceipt.contractAddress };
    } catch (error: any) {
      this.web3Store.update({ isTransaction: false });
      if (error.data?.message) {
        this.web3Store.update({ errorMessageKey: error.data.message });
      }
      return this.handleViemErrors(error as any);
    }
  }

  /**
   * Mint multiple tokens
   */
  async mintMultiple(
    abi: any,
    contractAddress: Address,
    chainId: number,
    tokenCount: number,
  ): Promise<any> {
    try {
      const signerAddress = await this.getAddress();
      if (!signerAddress) throw new Error('Signer address not found');
      const tx = await this.writeContract({
        abi,
        contractAddress: contractAddress,
        args: [signerAddress, tokenCount],
        functionName: 'mintMultiple',
        chainId,
      });
      return tx;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }

  /**
   * Mint to a specific address
   */
  async mintTo(abi: any, contractAddress: Address, chainId: number): Promise<TransactionReceipt> {
    try {
      const signerAddress = await this.getAddress();
      if (!signerAddress) throw new Error('Signer address not found');

      const tx = await this.writeContract({
        abi,
        contractAddress: contractAddress,
        args: [signerAddress],
        functionName: 'mintTo',
        chainId,
      });
      return tx;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Safe transfer from
   */
  async safeTransferFrom(
    abi: any,
    contractAddress: Address,
    destination: Address,
    tokenId: number,
    chainId: number,
  ): Promise<any> {
    try {
      const signerAddress = await this.getAddress();
      if (!signerAddress) throw new Error('Signer address not found');

      const tx = await this.writeContract({
        abi,
        contractAddress: contractAddress,
        args: [signerAddress, destination, tokenId],
        functionName: 'transferFrom',
        chainId,
      });
      return tx;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Burn a token
   */
  async burn(
    abi: any,
    contractAddress: Address,
    tokenId: number,
    chainId: number,
  ): Promise<TransactionReceipt> {
    try {
      const signerAddress = await this.getAddress();
      if (!signerAddress) throw new Error('Signer address not found');
      const tx = await this.writeContract({
        abi,
        contractAddress: contractAddress,
        args: [signerAddress, tokenId],
        functionName: 'burn',
        chainId,
      });
      return tx;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private async writeContract({
    abi,
    contractAddress,
    chainId,
    functionName,
    args,
  }: {
    abi: string;
    contractAddress: Address;
    chainId: number;
    functionName: string;
    args: any[];
  }): Promise<TransactionReceipt> {
    this.web3Store.resetLastTransaction();
    this.web3Store.setIsTransaction(true);
    try {
      const { request } = await this.publicClient.simulateContract({
        address: contractAddress,
        abi: JSON.parse(abi),
        functionName,
        args,
        account: await this.getAccount(),
        chain: this.getChain(chainId),
      });
      const hash = await this.walletClient.writeContract(request as any);
      this.web3Store.setTxHash(hash);
      const transaction = await this.publicClient.waitForTransactionReceipt({ hash });
      this.web3Store.setIsTransaction(false);
      this.web3Store.setTxReceipt(transaction);
      return transaction;
    } catch (e) {
      this.web3Store.setIsTransaction(false);
      return this.handleViemErrors(e as any);
    }
  }

  private async readContract<T>({
    abi,
    contractAddress,
    functionName,
    chainId,
    args,
  }: {
    abi: any;
    contractAddress: Address;
    functionName: string;
    chainId: number;
    args?: any[];
  }): Promise<T> {
    try {
      this.web3Store.setIsFetchingData(true);
      await this.changeChainIfNeeded(chainId);
      const result: T = (await this.publicClient.readContract({
        address: contractAddress,
        abi: JSON.parse(abi),
        functionName,
        args: args?.length ? args : [],
      })) as T;
      this.web3Store.setIsFetchingData(false);
      return result;
    } catch (error: unknown) {
      this.web3Store.setIsFetchingData(false);
      return this.handleViemErrors(error as any);
    }
  }

  private handleViemErrors(error: ErrorType) {
    console.error(error);

    // Determine the error name and code
    const errorName = (error as any).name;
    const errorCode = (error as any).code;

    // Handle specific error types
    switch (errorName) {
      case 'AccountNotFoundError':
        // No account provided for an action that requires one
        this.alertService.warning('No account provided. Please connect your wallet.');
        break;

      case 'InvalidAddressError':
        // Provided address is invalid
        this.alertService.warning('The provided address is invalid. Please check and try again.');
        break;

      case 'BlockNotFoundError':
        // Specified block could not be found
        this.alertService.warning('The specified block was not found. It might not exist.');
        break;

      case 'ChainDisconnectedError':
        // Disconnected from the blockchain network
        this.alertService.warning(
          'Disconnected from the blockchain network. Please check your connection.',
        );
        break;

      case 'ExecutionRevertedError':
        // Transaction execution reverted
        this.alertService.warning(
          'Transaction execution reverted. Please check the contract logic.',
        );
        break;

      case 'InsufficientFundsError':
        // Not enough funds to complete the transaction
        this.alertService.warning(
          'Insufficient funds. Please ensure your account has enough balance.',
        );
        break;

      case 'NonceTooLowError':
        // Nonce provided is too low
        this.alertService.warning(
          'Nonce too low. Please ensure your transaction nonce is correct.',
        );
        break;

      case 'TimeoutError':
        // Request timed out
        this.alertService.warning('The request timed out. Please try again later.');
        break;

      case 'UnsupportedProviderMethodError':
        // Method not supported by the provider
        this.alertService.warning('The requested method is not supported by the provider.');
        break;

      case 'UserRejectedRequestError':
        // User rejected the request (e.g., in wallet)
        this.alertService.warning('Request rejected. Please authorize the request in your wallet.');
        break;

      default:
        // Handle unknown errors
        this.alertService.warning(
          error.message ? error.message : 'An unknown error occurred. Please try again.',
        );
        break;
    }

    // Reject the promise with the error
    return Promise.reject(error);
  }
}
